import { useEffect, useState, useCallback } from "react";
import StockItemView from "@/pages/StockItemView";
import GroupSummaryView from "@/pages/GroupSummaryView";
import { StockTransfersView, PhysicalStockView, MovementAnalysisView, AgeingView } from "@/pages/reports/InventoryReports";

const FULL_VIEWS = ["stock-item", "group-summary", "stock-transfers", "physical-stock-register", "movement-analysis", "ageing-analysis"];

const VIEW_COMPONENTS = {
  "stock-item": StockItemView,
  "group-summary": GroupSummaryView,
  "stock-transfers": StockTransfersView,
  "physical-stock-register": PhysicalStockView,
  "movement-analysis": MovementAnalysisView,
  "ageing-analysis": AgeingView,
};

const ITEMS = [
  { key: "stock-item", label: "Stock Item", hot: "S", hotIdx: 0, gapBefore: false },
  { key: "group-summary", label: "Group Summary", hot: "G", hotIdx: 0, gapBefore: false },
  { key: "stock-transfers", label: "STock Transfers", hot: "T", hotIdx: 1, gapBefore: true, plain: "Stock Transfers" },
  { key: "physical-stock-register", label: "Physical Stock Register", hot: "P", hotIdx: 0, gapBefore: false },
  { key: "movement-analysis", label: "Movement Analysis", hot: "M", hotIdx: 0, gapBefore: true },
  { key: "ageing-analysis", label: "Ageing Analysis", hot: "A", hotIdx: 0, gapBefore: false },
  { key: "quit", label: "Quit", hot: "Q", hotIdx: 0, gapBefore: true },
];

export default function InventoryBooksView({ onClose }) {
  const [selected, setSelected] = useState(0);
  const [openItem, setOpenItem] = useState(null);

  const activate = useCallback((idx) => {
    const item = ITEMS[idx];
    if (item.key === "quit") { onClose(); return; }
    setOpenItem(item);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (openItem) {
        if (e.key === "Escape" && !FULL_VIEWS.includes(openItem.key)) { e.preventDefault(); e.stopPropagation(); setOpenItem(null); }
        return;
      }
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => (s + 1) % ITEMS.length); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => (s - 1 + ITEMS.length) % ITEMS.length); }
      else if (e.key === "Enter") { e.preventDefault(); activate(selected); }
      else if (/^[a-zA-Z]$/.test(e.key) && !e.ctrlKey && !e.altKey && !e.metaKey) {
        const idx = ITEMS.findIndex((m) => m.hot.toLowerCase() === e.key.toLowerCase());
        if (idx >= 0) { e.preventDefault(); setSelected(idx); activate(idx); }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [selected, openItem, activate, onClose]);

  if (openItem && VIEW_COMPONENTS[openItem.key]) {
    const View = VIEW_COMPONENTS[openItem.key];
    return <View onClose={() => setOpenItem(null)} />;
  }

  return (
    <div className="flex justify-center items-start pt-6 pb-10 min-h-[70vh]" data-testid="inventory-books-view">
      <div className="w-full max-w-md rounded-md overflow-hidden shadow-xl" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3" }}>
        <div className="text-white text-center py-2 text-sm font-bold tracking-widest uppercase" style={{ background: "#0f766e" }} data-testid="ib-header">
          Inventory Books
        </div>
        <div className="py-8 px-10 select-none" data-testid="ib-menu">
          {ITEMS.map((item, idx) => {
            const text = item.plain || item.label;
            return (
              <div key={item.key} className={item.gapBefore ? "mt-5" : "mt-0.5"}>
                <button
                  data-testid={`ib-menu-${item.key}`}
                  onClick={() => { setSelected(idx); activate(idx); }}
                  onMouseEnter={() => setSelected(idx)}
                  className={`w-full text-left px-3 py-1 text-[15px] font-semibold rounded-sm transition-colors ${
                    selected === idx ? "bg-teal-600 text-white shadow-sm" : "text-slate-800 hover:bg-[#f1e9d6]"
                  }`}
                >
                  <span>
                    {text.slice(0, item.hotIdx)}
                    <span className="text-red-600 font-bold">{text[item.hotIdx]}</span>
                    {text.slice(item.hotIdx + 1)}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>

      {openItem && !FULL_VIEWS.includes(openItem.key) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm" data-testid="ib-view-overlay" onClick={() => setOpenItem(null)}>
          <div className="bg-white border-2 border-teal-700 shadow-2xl w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="text-white px-4 py-2 text-sm font-bold" style={{ background: "#0f766e" }} data-testid="ib-view-title">
              {openItem.plain || openItem.label}
            </div>
            <div className="p-8 text-center text-sm text-slate-600" data-testid="ib-view-body">
              This report view is being prepared and will be available soon.
              <div className="mt-4">
                <button onClick={() => setOpenItem(null)} data-testid="ib-view-close"
                  className="px-4 py-1.5 text-white text-xs font-bold rounded-sm" style={{ background: "#0f766e" }}>
                  Close (Esc)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
